import { edmCampaign } from '@bingads-webui/edm-campaign';

export const createOrUpdateCampaign = (data) => {
  const campaignEDM = edmCampaign.types.resolve('Model.ShoppingCampaign');
  const campaignResource = new campaignEDM.ResourceIdentifier();
  const createOrUpdateCall = data && data.Id ?
    campaignResource.bulkUpdate.bind(campaignResource) :
    campaignResource.bulkCreate.bind(campaignResource);
  return createOrUpdateCall(data);
};

export const deleteCampaign = (id) => {
  const campaignEDM = edmCampaign.types.resolve('Model.ShoppingCampaign');
  const campaignResource = new campaignEDM.ResourceIdentifier();
  const deleteCall = campaignResource.bulkDestroy.bind(campaignResource);
  return deleteCall(id);
};

export const readCampaign = (cid, aid, campaignId, {
  // `includeTextGuidelines` is a legacy alias that enables both fields. Prefer the
  // per-field flags so each pilot is gated at the source: a field is fetched (and
  // therefore present in the view model) only when its pilot is enabled, letting the
  // model conversions stay permission-agnostic.
  includeTextGuidelines = false,
  includeTermExclusions = includeTextGuidelines,
  includeMessagingRestrictions = includeTextGuidelines,
} = {}) => {
  const queryParams = {
    select: ['CampaignBrandKit/BrandColor', 'Budget', 'CampaignConversionGoal/Goals'],
  };
  if (includeTermExclusions || includeMessagingRestrictions) {
    const expands = ['CampaignBrandKit/BrandColor', 'Budget', 'CampaignConversionGoal/Goals'];
    if (includeTermExclusions) {
      expands.push('CampaignBrandKit/TermExclusions');
    }
    if (includeMessagingRestrictions) {
      expands.push('CampaignBrandKit/MessagingRestrictions');
    }
    queryParams.$expand = expands.join(',');
  }
  return edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .get(queryParams)
    .toPromise();
};

// Read campaigns with PerformanceMax campaign type
export const readCampaigns = (cid, aid) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns
    .get({
      filter: {
        where: 'CampaignType eq \'PerformanceMax\'',
      },
    })
    .toPromise();

export const readAudienceAssociations = (cid, aid, campaignId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AudienceAssociations
    .get({
      bypassCache: true,
      top: 2000,
    })
    .toPromise();

const campaignCriterionTypeName = 'Model.CampaignCriterion';

export const readCampaignCriterions = (cid, aid, campaignId, type, bypassCache = false) => {
  const criterions = edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .CampaignCriterions
    .$cast(campaignCriterionTypeName);
  const query = { filter: { where: type } };

  // The OData client only honors bypassCache as the third argument of get().
  return (bypassCache ? criterions.get(query, undefined, true) : criterions.get(query)).toPromise();
};

export const readLocationCriterions = (cid, aid, campaignId, bypassCache = false) =>
  readCampaignCriterions(
    cid, aid, campaignId,
    "isof(Criterion,'Model.LocationCriterion') or isof(Criterion,'Model.RadiusCriterion') or isof(Criterion,'Model.LocationIntentCriterion')",
    bypassCache
  );

export const readDayTimeCriterions = (cid, aid, campaignId) =>
  readCampaignCriterions(
    cid, aid, campaignId,
    "isof(Criterion,'Model.DayTimeCriterion')"
  );

export const readAgeCriterions = (cid, aid, campaignId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .CampaignCriterions
    .$cast(campaignCriterionTypeName)
    .get({
      bypassCache: true,
      filter: {
        where: "isof(Criterion,'Model.AgeCriterion')",
      },
    })
    .toPromise();

export const readGenderCriterions = (cid, aid, campaignId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .CampaignCriterions
    .$cast(campaignCriterionTypeName)
    .get({
      bypassCache: true,
      filter: {
        where: "isof(Criterion,'Model.GenderCriterion')",
      },
    })
    .toPromise();

export const readDeviceCriterions = (cid, aid, campaignId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .CampaignCriterions
    .$cast(campaignCriterionTypeName)
    .get({
      bypassCache: true,
      filter: {
        where: "isof(Criterion,'Model.DeviceCriterion')",
      },
    })
    .toPromise();

export const createOrUpdateAssetGroup = (data) => {
  const assetGroupEDM = edmCampaign.types.resolve('Model.AssetGroup');
  const assetGroupResource = new assetGroupEDM.ResourceIdentifier();
  const createOrUpdateCall = data && data.Id ?
    assetGroupResource.bulkUpdate.bind(assetGroupResource) :
    assetGroupResource.bulkCreate.bind(assetGroupResource);
  return createOrUpdateCall(data);
};

export const createOrUpdateListingGroup = (itemsToAdd, itemsToDelete) => {
  const listingGroupEDM = edmCampaign.types.resolve('Model.ProductListingGroup');
  const listingGroupResource = new listingGroupEDM.ResourceIdentifier();
  const data = itemsToDelete.concat(itemsToAdd);
  const createOrUpdateCall = data && data.Id ?
    listingGroupResource.bulkUpdate.bind(listingGroupResource) :
    listingGroupResource.bulkCreate.bind(listingGroupResource);
  return createOrUpdateCall(data);
};

export const readAssetGroup = (cid, aid, campaignId, assetGroupId, isSearchThemeEnabled, isUrlRulesEnabled = false) => {
  const toSelect = ['Headlines', 'LongHeadlines', 'Descriptions', 'AudienceGroup', 'Images', 'Videos'];

  if (isSearchThemeEnabled) toSelect.push('AssetGroupSearchThemes');
  if (isUrlRulesEnabled) toSelect.push('AssetGroupUrlTargets');

  return edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AssetGroups.$withKey(assetGroupId)
    .get({ select: toSelect })
    .toPromise();
};

// List the asset groups under a campaign. Used to recover an asset group Id
// when it is missing from scope but the campaign already has one, so a save
// can UPDATE the existing asset group instead of creating a duplicate.
export const readAssetGroups = (cid, aid, campaignId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AssetGroups['Default.GridData']
    .$makeCall({
      query: {
        count: true,
        select: ['Id'],
        top: 20,
      },
    })
    .toPromise();

export const readCampaignNegativeWebpages = (cid, aid, campaignId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .CampaignNegativeWebpages
    .get({
      top: 10000,
      skip: 0,
    })
    .toPromise();

export const readConversionGoalDetails = (cid, aid) => {
  if (!cid || !aid) return Promise.resolve(null);
  return edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)['Default.ConversionGoalDetails']
    .$makeCall()
    .toPromise();
};

const ACTIVE_AD_EXTENSION_FILTER = "(AdExtension/Status eq Enum.AdExtensionStatus'Active')";

export const readExtensionAssociations = (cid, aid, campaignId, payload, top = 20) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AdExtensionAssociations['Default.GridData']
    .$makeCall({
      query: {
        top,
        filter: { where: ACTIVE_AD_EXTENSION_FILTER },
        select: ['AdExtension'],
      },
      payload,
    })
    .toPromise();

export const readAccountExtensionAssociations = (cid, aid, payload, top = 20) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .AdExtensionAssociations['Default.GridData']
    .$makeCall({
      query: {
        top,
        filter: { where: ACTIVE_AD_EXTENSION_FILTER },
        select: ['AdExtension'],
      },
      payload,
    })
    .toPromise();

export const readCampaignExtensionAssociations = (cid, aid, campaignId, payload, top = 20) =>
  readExtensionAssociations(cid, aid, campaignId, payload, top);

export const readCampaignDeliveryDetails = (cid, aid, campaignId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns['Default.GridData']
    .$makeCall({
      query: {
        top: 1,
        filter: { where: `Id eq ${campaignId}` },
        select: ['Id', 'DeliveryStatus', 'DeliveryStatusDetails', 'IsBidLandscapeAvailable'],
      },
      payload: { gridDataParameters: {} },
    })
    .toPromise();


export const readNegativeKeywords = (cid, aid, campaignId, top = 1000) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .NegativeKeywords['Default.GetCampaignNegativeKeywords']
    .$makeCall({
      query: {
        count: true,
        top,
      },
      payload: { gridDataParameters: {} },
    })
    .toPromise();

export const createOrUpdateNegativeKeywords = (data) => {
  const negKeywordsEDM = edmCampaign.types.resolve('Model.CampaignNegativeKeyword');
  const negKeywordsResource = new negKeywordsEDM.ResourceIdentifier();
  const createOrUpdateCall = data && data.Id ?
    negKeywordsResource.bulkUpdate.bind(negKeywordsResource) :
    negKeywordsResource.bulkCreate.bind(negKeywordsResource);
  return createOrUpdateCall(data);
};

// Excluded Content Terms (Account Content Negative Keywords)

export const readExcludedContentTermsLists = (cid, aid) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .AccountContentNegativeKeywordLists
    .get({
      bypassCache: true,
      query: { count: true, top: 20, cacheBust: Date.now() },
    })
    .toPromise();

export const readExcludedContentTermsByListId = (cid, aid, listId, top = 10000) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .NegativeKeywordLists.$withKey(listId)
    .NegativeKeywords
    .get({
      bypassCache: true,
      top,
      count: true,
      query: { cacheBust: Date.now() },
    })
    .toPromise();

// Kept for backward compatibility
export const readExcludedContentTerms = (cid, aid, top = 1000) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .AccountContentNegativeKeywordLists
    .get({
      query: {
        count: true,
        top,
        expand: 'AccountContentNegativeKeywords,AccountContentNegativeKeywordListAssociations',
      },
    })
    .toPromise();

export const deleteExcludedContentTerms = (cid, aid, termIds, listId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .bulkUpdateMany(
      termIds,
      [{
        AccountContentNegativeKeywordLists: {
          Name: 'Account Content Negative Keyword List',
          Id: listId,
          AccountContentNegativeKeywords: termIds.map(id => ({
            ParentId: listId,
            Id: id,
            Status: 'Deleted',
          })),
        },
      }]
    );

// The payload shape determines create vs. upsert behavior
export const bulkCreateExcludedContentTerms = (cid, aid, payload) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .bulkCreate(payload);

export const readAudienceGroupsByName = (cid, aid, name) => {
  const escapedName = String(name).replace(/'/g, "''");
  return edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .AudienceGroups
    .get({ filter: { where: `Name eq '${escapedName}'` } })
    .toPromise()
    .then(res => (res && res.value) || []);
};
